import { FeesRepo } from "@services/fees.repo";
import { PortfolioRepo } from "@services/portfolio.repo";
import { TableColumn } from "@models/components/tableColumn";
import * as moment from "moment";
import * as _ from "lodash";
import * as XLSX from "xlsx";
import angular = require("angular");

export class DebitAccountsController {
  public clientFeeCashAccounts: Array<any>;
  public clientCashAccountsGroupedByCurrency: Array<any>;
  public clientCashAccounts: Array<any>;
  public client: any;
  public onRefresh: () => void;
  public canEdit: boolean = true; // Default to true for backward compatibility

  // Reusable table properties
  public debitAccountColumns: TableColumn[] = [
    { key: 'portfolioNumber', label: 'Portfolio Number', type: 'text', width: 'col-md-2' },
    { key: 'currency', label: 'Currency', type: 'text', width: 'col-md-1' },
    { key: 'accountNumber', label: 'Account Number', type: 'text', width: 'col-md-3' },
    { key: 'addedByFormatted', label: 'Added By', type: 'text', width: 'col-md-2' },
    { key: 'modifiedByFormatted', label: 'Modified by', type: 'text', width: 'col-md-2' }
  ];

  /** @ngInject */
  constructor(
    private feesRepo: FeesRepo,
    private portfolioRepo: PortfolioRepo,
    private notifyjs: any,
    private $scope: angular.IScope,
    private $mdDialog: angular.material.IDialogService,
    private $rootScope: angular.IRootScopeService
  ) {
    this.$scope.$on("exportDebitAccountsExcel", () => {
      this.downloadDebitAccountsExcel();
    });
  }

  $onInit = () => {
    if (this.client && this.client.portfolios) {
      this.getClientCashAccounts();
      this.getDefaultFeeCashAccounts();
    }
  };

  $onChanges = (changes: any) => {
    if (changes.client && changes.client.currentValue && changes.client.currentValue.portfolios) {
      this.getClientCashAccounts();
      this.getDefaultFeeCashAccounts();
    }
  };

  convertToLocaleTime = (date: any): string => {
    if (!date) return '';
    return moment(new Date(date)).format("DD-MM-YYYY");
  };

  getClientCashAccounts = () => {
    if (!this.client || !this.client.portfolios) {
      return;
    }
    let portfolioIds = this.client.portfolios.map(
      (portfolio: any) => portfolio.portfolioId
    );
    this.portfolioRepo
      .getPortfolioCashAccounts(portfolioIds)
      .then((res) => {
        this.clientCashAccounts = res.data as Array<any>;
        this.clientCashAccountsGroupedByCurrency = _.chain(
          this.clientCashAccounts
        )
          .groupBy("currency")
          .toPairs()
          .map(function (currentItem) {
            return _.zipObject(["currency", "items"], currentItem);
          })
          .value();
        
        // After clientCashAccounts is loaded, refresh fee cash accounts to populate currency/accountNumber
        this.refreshFeeCashAccountsDisplay();
      })
      .catch((err) => {});
  };

  getDefaultFeeCashAccounts = () => {
    if (!this.client || !this.client.id) {
      return;
    }
    this.feesRepo
      .getClientFeeCashAccounts(this.client.id)
      .then((res) => {
        // Store raw data first, then format if clientCashAccounts is available
        const rawAccounts = res.data as Array<any>;
        if (this.clientCashAccounts && this.clientCashAccounts.length > 0) {
          this.clientFeeCashAccounts = this.formatAccountsForDisplay(rawAccounts);
        } else {
          // Store raw data, will be formatted when clientCashAccounts loads
          this.clientFeeCashAccounts = rawAccounts;
        }
      })
      .catch((err) => {});
  };

  refreshFeeCashAccountsDisplay = () => {
    if (this.clientFeeCashAccounts && this.clientFeeCashAccounts.length > 0 && this.clientCashAccounts && this.clientCashAccounts.length > 0) {
      this.clientFeeCashAccounts = this.formatAccountsForDisplay(this.clientFeeCashAccounts);
    }
  };

  /** Refresh debit accounts data (used by reusable-table refresh button). */
  refreshDebitAccounts = () => {
    if (this.client && this.client.portfolios) {
      this.getClientCashAccounts();
      this.getDefaultFeeCashAccounts();
    }
  };

  downloadDebitAccountsExcel = () => {
    if (!this.debitAccountColumns || this.debitAccountColumns.length === 0) {
      this.notifyjs("No columns defined for export", "warning");
      return;
    }

    if (!this.clientFeeCashAccounts || this.clientFeeCashAccounts.length === 0) {
      this.notifyjs("No debit accounts available to export", "warning");
      return;
    }

    try {
      const workbook = XLSX.utils.book_new();

      const headers = this.debitAccountColumns.map((column) => column.label);

      const dataRows = this.clientFeeCashAccounts.map((item: any) => {
        return this.debitAccountColumns.map((column) => {
          const value = item[column.key];

          if (value === null || value === undefined) {
            return "";
          }

          if (column.type === "date" && value) {
            return new Date(value).toLocaleDateString();
          }

          if (column.type === "boolean") {
            return value ? "Yes" : "No";
          }

          return value.toString();
        });
      });

      const worksheetData = [headers, ...dataRows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      const columnWidths = this.debitAccountColumns.map((column) => {
        let width = 15;
        if (column.width) {
          const widthMatch = column.width.toString().match(/\d+/);
          if (widthMatch) {
            width = parseInt(widthMatch[0]) * 2;
          }
        }
        return { wch: Math.max(width, 10) };
      });
      (worksheet as any)["!cols"] = columnWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, "DebitAccounts");

      const fileName =
        "debit_accounts_" +
        new Date().toISOString().split("T")[0] +
        ".xlsx";

      XLSX.writeFile(workbook, fileName);

      this.notifyjs("Debit accounts Excel downloaded successfully", "success");
    } catch (error) {
      console.error("Error exporting debit accounts to Excel:", error);
      this.notifyjs(
        "Error exporting debit accounts to Excel. Please ensure xlsx library is installed.",
        "error"
      );
    }
  };

  formatAccountsForDisplay = (accounts: Array<any>): Array<any> => {
    return accounts.map((account: any) => {
      const formatted = { ...account };
      
      // Populate currency and accountNumber from clientCashAccounts if accountId exists
      if (account.accountId && this.clientCashAccounts && this.clientCashAccounts.length > 0) {
        const selectedAccount = this.clientCashAccounts.find((cashAccount: any) => {
          return cashAccount.id == account.accountId;
        });
        if (selectedAccount) {
          formatted.currency = selectedAccount.currency;
          formatted.accountNumber = selectedAccount.accountNumber;
        }
      }
      
      // Format Added By column with HTML line break
      if (account.addedBy) {
        formatted.addedByFormatted = `${account.addedBy}<br>on ${this.convertToLocaleTime(account.dateAdded)}`;
      } else {
        formatted.addedByFormatted = '';
      }
      
      // Format Modified by column with HTML line break
      if (account.modifiedBy) {
        formatted.modifiedByFormatted = `${account.modifiedBy}<br>on ${this.convertToLocaleTime(account.dateModified)}`;
      } else {
        formatted.modifiedByFormatted = '';
      }
      
      return formatted;
    });
  };

  editPortfolioFeeCashAccount = (account: any) => {
    if (!this.canEdit) return;
    
    const tempScope = this.$rootScope.$new(true) as any;
    tempScope.account = angular.copy(account);
    tempScope.clientCashAccounts = this.clientCashAccounts;
    tempScope.clientCashAccountsGroupedByCurrency = this.clientCashAccountsGroupedByCurrency;

    this.$mdDialog
      .show({
        template: `<md-dialog flex="30"><edit-debit-account-dialog account="account" client-cash-accounts="clientCashAccounts" client-cash-accounts-grouped-by-currency="clientCashAccountsGroupedByCurrency"></edit-debit-account-dialog></md-dialog>`,
        clickOutsideToClose: true,
        scope: tempScope,
      })
      .then((result) => {
        if (result) {
          this.getDefaultFeeCashAccounts();
          if (this.onRefresh) {
            this.onRefresh();
          }
        }
      })
      .catch(() => {
        // User cancelled, do nothing
      });
  };
}

export const DebitAccountsComponent = {
  template: require('./debitAccounts.html'),
  controller: DebitAccountsController,
  styles: require('./debitAccounts.scss'),
  controllerAs: '$ctrl',
  bindings: {
    client: '<',
    onRefresh: '&', // Called to refresh the data
    canEdit: '<' // Whether user can edit debit accounts
  }
};


// WEBPACK FOOTER //
// ./src/app/fees/debitAccounts/debitAccounts.ts